import React from 'react';
import PropTypes from 'prop-types';
import {
    SearchSuggestion,
    SearchCategory
} from 'components';

import {
    trackSearchCategoryChanges,
    trackSearch,
    trackSubmitButton,
    trackResetButton
} from './search.analytics';

// import CatPlaceholder from 'components/search-category/category-placeholder';
import Storage from '../../app/data/storage-service';
import axios from 'axios';
import logger from 'packages/logger';
import debounce from 'packages/debounce';
import { getExistingQueryString } from './helper';

const RecentSearches = new Storage('search-recent');
let CancelToken = axios.CancelToken;
let cancel;
const optionDivider = {
    id: 0,
    isTopLevel: false,
    name: '------------------',
    selected: false
};

const optionAllCategories = {
    id: 0,
    isTopLevel: false,
    name: 'Alla kategorier',
    selected: false
};

const KEYCODE_ENTER = 13;
const KEYCODE_ESC = 27;
const KEYCODE_UP = 38;
const KEYCODE_DOWN = 40;
const NAVIGATION_KEY_CODES = [KEYCODE_DOWN, KEYCODE_UP, KEYCODE_ENTER, KEYCODE_ESC];
const minCharsForSuggestions = 2;

const selectGenerator = (allCategories, categoryPath) => {
    let selectOptions = [];
    if (categoryPath && categoryPath.length > 2) {
        selectOptions = selectOptions.concat(categoryPath);
        selectOptions.push(optionDivider);
    } else {
        selectOptions.push(optionAllCategories);
    }
    return selectOptions.concat(allCategories);
};

class Search extends React.Component {

    constructor(props) {
        super(props);

        const categoryInformation = this.getCategoryInformation(props.initData);
        this.selectedCategoryOnInit = categoryInformation.selectedCategory;

        this.getSuggestions = debounce(this.getSuggestions, 200);
        this.setSearchSuggestionsElement = this.setSearchSuggestionsElement.bind(this);
        // this.getCategoryFromId = this.getCategoryFromId.bind(this);
        this.searchSuggestionsElement = null;

        this.state = {
            searchTerm: props.searchTerm || '',
            initialSearchTerm: props.searchTerm || '',
            userInteracted: false,
            isSearchInputFocussed: false,
            isAutomaticallyFocussed: false,
            previousSearches: this.getRecentSearches(),
            searchSuggestions: {
                selectedIndex: null,
                previousSearches: this.getFilteredPreviousSearches(props.searchTerm),
                searches: [],
                shops: []
            },
            selectedCategoryId: this.selectedCategoryOnInit ? this.selectedCategoryOnInit.id : 0
        };
    }

    componentWillReceiveProps(nextProps) {
        const currCategoryInformation = this.getCategoryInformation(this.props.initData);
        const nextCategoryInformation = this.getCategoryInformation(nextProps.initData);
        const currSelectedId = currCategoryInformation.selectedCategory ? currCategoryInformation.selectedCategory.id : 0;
        const nextSelectedId = nextCategoryInformation.selectedCategory ? nextCategoryInformation.selectedCategory.id : 0;

        if (currSelectedId !== nextSelectedId) {
            this.setState(prevState => {
                return {
                    ...prevState,
                    selectedCategoryId: nextSelectedId
                };
            });
        }
    }

    getCategoryInformation = initData => {
        let selectedCategory = null;
        let categoryPath = initData.categoryPath;

        if (initData.categoryPath && initData.categoryPath.length > 0) {
            selectedCategory = initData.categoryPath.find(cat => {
                return cat.selected === true;
            });

            if (selectedCategory.id === 0) { // hack because backend is buggy when categoryId = 0
                selectedCategory.name = 'Alla kategorier';
                categoryPath = [];
            }
        }

        return {
            selectedCategory,
            categoryPath
        }
    }

    getRecentSearches = () => {
        let recent = RecentSearches.fetch().orderByTimestamp();
        return recent.splice(0, 7);
    }

    normalizeSearches = (searches = []) => {
        return searches.reduce((acc, search) => {
            if (search.categories) {
                search.categories.map(category => {
                    acc.push({
                        text: search.text,
                        category: category
                    });
                })
            } else {
                acc.push({
                    text: search.text,
                    category: null
                });
            }
            return acc;
        }, []);
    }

    getSuggestions = searchTerm => {
        if (cancel !== undefined) {
            cancel();
        }
        axios.get(`/search/suggestions`,
            {
                cancelToken: new CancelToken(function executor(c) {
                    cancel = c;
                }),
                params: {
                    q: searchTerm
                }
            })
            .then(response => {
                const normalizedSearches = this.normalizeSearches(response.data.searches); // Since the searches can exist in multiple categories we need to flatten the array
                this.setState({
                    searchSuggestions: {
                        selectedIndex: null,
                        previousSearches: this.state.searchSuggestions.previousSearches,
                        searches: normalizedSearches || [],
                        shops: response.data.shops || []
                    }
                });
            })
            .catch(error => {
                const result = error.response;
                logger(error);
                return Promise.reject(result);
            });
    }

    handleUpDownArrowKeys = keyCode => {
        const searchSuggestions = this.state.searchSuggestions;
        const nrOfSuggestions = () => {
            if (this.state.searchTerm.length >= minCharsForSuggestions) {
                return searchSuggestions.previousSearches.length + searchSuggestions.searches.length + searchSuggestions.shops.length + 1; // +1 is for the hard coded advanced search link
            } else {
                return searchSuggestions.previousSearches.length + 1; // see above for + 1 explaination
            }
        };

        const selectedIndex = searchSuggestions.selectedIndex;
        let newSelectedIndex = null;
        if (keyCode === KEYCODE_DOWN) {
            const currentIndex = selectedIndex === null ? -1 : selectedIndex;
            newSelectedIndex = (currentIndex + 1) > (nrOfSuggestions() - 1) ? 0 : currentIndex + 1;
            // console.log('newSelectedIndex', newSelectedIndex);
        } else if (keyCode === KEYCODE_UP) {
            const currentIndex = selectedIndex === null ? -1 : selectedIndex;
            newSelectedIndex = (currentIndex - 1) < 0 ? nrOfSuggestions() - 1 : currentIndex - 1;
        }
        // console.log('newSelectedIndex', newSelectedIndex);

        this.setState({
            searchSuggestions: {
                ... this.state.searchSuggestions,
                selectedIndex: newSelectedIndex
            }
        });
    }

    handleUserKeyUp = evt => {
        if (NAVIGATION_KEY_CODES.indexOf(evt.which) < 0) {
            return;
        }

        if (evt.which === KEYCODE_ESC) {
            evt.target.blur();
            // TODO: set this.state.searchTerm to this.state.initialSearchTerm?
            return;
        }

        if (evt.which === KEYCODE_ENTER) {
            const suggestions = this.searchSuggestionsElement ? this.searchSuggestionsElement.querySelectorAll('[data-suggestion]') : null;
            if (suggestions && suggestions[this.state.searchSuggestions.selectedIndex]) {
                evt.preventDefault();
                suggestions[this.state.searchSuggestions.selectedIndex].click();
            }
            return;
        }

        if (evt.which === KEYCODE_DOWN || evt.which === KEYCODE_UP) {
            this.handleUpDownArrowKeys(evt.which);
        }
    }

    getFilteredPreviousSearches = (searchTerm, limit = 7) => {
        const prev = this.getRecentSearches();

        if (!!searchTerm) {
            return prev ? prev
                .filter(item => {
                    //if no searchterm or searchterm matches in history include this item
                    if (searchTerm.length > 0 && item.key.includes(searchTerm) || searchTerm.length === 0) {
                        return item;
                    } else {
                        return false;
                    }
                }).splice(0, limit) : [];
        }
        return prev ? prev.splice(0, limit) : [];
    }

    handleUserInputChange = evt => {
        // If searchTerm.length === 0 ---> show search history with a maximum of 7 items
        // If searchTerm.length > 0 && < 2 ---> show matching search history with a maximum of 3
        // If searchTerm.length > 1 ---> show matching history with a maximum of three + everything returned from backend

        const searchTerm = evt.target.value;
        let filteredPreviousSearches = [];
        if (searchTerm.length < 1) {
            filteredPreviousSearches = this.getFilteredPreviousSearches(searchTerm, 7);
        } else {
            filteredPreviousSearches = this.getFilteredPreviousSearches(searchTerm, 3);
        }

        //@TODO validation
        let newState = {
            ... this.state,
            searchTerm: searchTerm,
            userInteracted: true,
            isAutomaticallyFocussed: false,
            previousSearches: this.getRecentSearches(),
            searchSuggestions: {
                ...this.state.searchSuggestions,
                previousSearches: filteredPreviousSearches
            }
        }

        if (searchTerm.length >= minCharsForSuggestions) {
            this.getSuggestions(searchTerm);
        } else {
            newState.searchSuggestions = {
                ...newState.searchSuggestions,
                searches: [],
                shops: []
            };
        }

        this.setState(newState);
    }

    handleBlur = () => {
        const onBlur = () => {
            this.setState({
                userInteracted: false,
                isSearchInputFocussed: false,
                isAutomaticallyFocussed: false
            });
        };

        window.setTimeout(onBlur, 100);
    }

    handleFocus = () => {
        this.setState({
            isSearchInputFocussed: true,
            userInteracted: true,
            searchSuggestions: {
                ...this.state.searchSuggestions,
                selectedIndex: null
            }
        });
    }

    addRecentSearch = () => {
        if (!this.state.searchTerm) {
            return;
        }

        const prettySearchTerm = this.state.searchTerm.toString()
            .replace(/</g, ' ')
            .replace(/>/g, ' ')
            .trim();

        if (prettySearchTerm.length > 2) {
            let recentCount = RecentSearches.get(prettySearchTerm) || 0;
            recentCount += 1;
            RecentSearches.set(prettySearchTerm, recentCount);
        }
    }

    handleFormSubmit = evt => {
        evt.preventDefault();
        this.addRecentSearch();
        this.setState({
            previousSearches: this.getRecentSearches()
        });

        const {
            searchTerm,
            initialSearchTerm
        } = this.state;

        // Query reformulation = First "Nike" and then "Nike air" or the other way around
        const isQueryReformulation = (searchTerm.indexOf(initialSearchTerm) === 0 || (initialSearchTerm && initialSearchTerm.indexOf(searchTerm) === 0));
        const searchParamsAsQueryStringParams = getExistingQueryString(this.props.searchUrl)
        const baseUrl = '/search?q=' + encodeURIComponent(searchTerm) + (this.state.selectedCategoryId ? '&categoryId=' + this.state.selectedCategoryId : '');
        const searchUrl = isQueryReformulation && searchParamsAsQueryStringParams ? baseUrl + '&' + searchParamsAsQueryStringParams : baseUrl;
        trackSearch();
        window.location.href = searchUrl;
        // publishEvent('ui:navigate', {href: searchUrl}); // TODO: If enabling this event we need to deal with the side effects, such as closing the search suggestions and still have the input in focus
    }

    handleClearSearchInput = () => {
        trackResetButton();
        this.setState({
            searchTerm: '',
            userInteracted: false
        });
    }

    handleCategoryChange = evt => {
        const selectBox = evt.target;
        const selectedValue = parseInt(selectBox.value, 10);

        trackSearchCategoryChanges(evt)
        this.setState(prevState => {
            return {
                ...prevState,
                selectedCategoryId: selectedValue
            };
        });
    }

    handleSuggestionsChangingSearchTerm = () => {
    }

    setSearchSuggestionsElement = el => {
        this.searchSuggestionsElement = el;
    }

    render() {
        const {
            allCategories,
            categoryPath
        } = this.props.initData;

        const selectOptions = selectGenerator(allCategories, categoryPath);
        const hasOptions = selectOptions.find(category => category.id === this.state.selectedCategoryId);
        const chosenCategory = hasOptions ? hasOptions : optionAllCategories;
        const placeHolderText = chosenCategory && this.state.selectedCategoryId !== 0 ? `Sök inom ${chosenCategory.name}` : 'Vad letar du efter?';

        return (
            <form action="/search"
                method="GET"
                autoComplete="off"
                onMouseOver={this.handleMouseOver}
                onMouseLeave={this.handleMouseLeave}
                onSubmit={this.handleFormSubmit}
                className="slim-header__search-form row no-gutters"
                data-nav-search >

                <div className="d-none d-md-flex col-md-auto">
                    <SearchCategory
                        handleCategoryChange={this.handleCategoryChange}
                        chosenCategory={chosenCategory}
                        selectedCategoryId={this.state.selectedCategoryId}
                        selectOptions={selectOptions} />
                </div>
                <div className="col">
                    <input type="text"
                        className="slim-header__search-field"
                        data-search-field
                        name="q"
                        placeholder={placeHolderText}
                        maxLength="98"
                        tabIndex="2"
                        ref={input => { this.searchInput = input; }}
                        onBlur={this.handleBlur}
                        role="search"
                        onFocus={this.handleFocus}
                        value={this.state.searchTerm}
                        onChange={this.handleUserInputChange}
                        onKeyDown={this.handleUserKeyUp} />
                    <fieldset className="slim-header__form-buttons">
                        {this.state.searchTerm ?
                            <button
                                type="reset"
                                className="slim-header__clear-button unbutton icon icon--close ss-fix--before d-md-none"
                                onClick={this.handleClearSearchInput} />
                            : null}
                        <button
                            type="submit"
                            data-search-submit
                            className="slim-header__submit-button flip-after unbutton"
                            onClick={trackSubmitButton}>Sök</button>
                    </fieldset>
                    {/* isSearchInputFocussed={this.state.isSearchInputFocussed} */}
                    <SearchSuggestion
                        filteredPreviousSearches={this.state.searchSuggestions.previousSearches}
                        suggestedSearches={this.state.searchSuggestions.searches}
                        suggestedShops={this.state.searchSuggestions.shops}
                        selectedIndex={this.state.searchSuggestions.selectedIndex}
                        searchTerm={this.state.searchTerm}
                        onChangeSearchTerm={this.handleSuggestionsChangingSearchTerm}
                        isSearchInputFocussed={this.state.isSearchInputFocussed}
                        setSearchSuggestionsElement={this.setSearchSuggestionsElement}
                        selectedCategoryId={this.state.selectedCategoryId}
                    />
                </div>
            </form>
        )
    }
};

Search.propTypes = {
    searchTerm: PropTypes.string,
    searchUrl: PropTypes.string,
    publishEvent: PropTypes.func
};

export default Search;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/search/search.jsx